import JSZip from "jszip";
import {jsPDF} from "jspdf";

import {getTemplateInstanceMetadata} from "../data/templateManifest";
import {buildArchiveFilename} from "./archiveFilename";

type TemplateMeta = {filename: string | null; dimensions: string | null; suffix: string | null};

export type ServerRenderedTemplate = {
  templateId: string;
  width: number;
  height: number;
  png: string;
  pdfPng?: string;
};

export type ServerRenderBatch = {templates: ServerRenderedTemplate[]; suffix?: string};

type FetchResponse = Pick<Response, "ok" | "status" | "json">;
type FetchServerRender = (input: string, init?: RequestInit) => Promise<FetchResponse>;

type ServerExportOptions = {
  batches: ServerRenderBatch[];
  product: string;
  channel: string;
  title?: string;
  set?: string;
};

type ExportBundle = {blob: Blob; filename: string; isSingleFile: boolean};

export async function sendGAEvent(eventCategory: string, eventAction: string, eventLabel: string) {
  const dataWindow = window as typeof window & {dataLayer?: Array<Record<string, string>>};
  dataWindow.dataLayer = dataWindow.dataLayer || [];
  dataWindow.dataLayer.push({event: "GAEvent", eventCategory, eventAction, eventLabel});
}

function findTemplateMeta(channel: string, templateId: string, product?: string): TemplateMeta {
  const metadata = getTemplateInstanceMetadata(channel, templateId, product);
  return {
    filename: metadata.filename ?? null,
    dimensions: metadata.dimensions,
    suffix: metadata.suffix ?? null,
  };
}

/**
 * The product a banner's file is named for: the code of an ALL-products
 * instance (`WhatsNewPreview-IJ`), else the export's product. Without it every
 * product's banner of the ALL view got one name.
 */
function filenameProduct(channel: string, templateId: string, product: string): string {
  const {id} = getTemplateInstanceMetadata(channel, templateId, product);
  return templateId.startsWith(`${id}-`) ? templateId.slice(id.length + 1) : product;
}

/** Is the banner one of the splash set's own? An added banner keeps its own name. */
function isSplashTemplate(templateId: string, product: string): boolean {
  return getTemplateInstanceMetadata("releases", templateId, product).set === SPLASH_SET;
}

const SPLASH_SET = "splash";
const SPLASH_FOLDER = "splash";

export const isSplashSetExport = (channel: string, set?: string) =>
  channel === "releases" && set === SPLASH_SET;

export const splashVariantFilename = (templateId: string) =>
  findTemplateMeta("releases", templateId).suffix === "@2x" ? "splash@2x.png" : "splash.png";

function buildTemplateFilename(
  templateId: string,
  width: number,
  height: number,
  product: string,
  channel: string
) {
  const meta = findTemplateMeta(channel, templateId, product);
  const dimensions = meta.suffix ? `${meta.dimensions}${meta.suffix}` : `${width}x${height}`;
  const fileProduct = filenameProduct(channel, templateId, product);
  const name = meta.filename
    ? `${fileProduct}-${meta.filename}-${dimensions}`
    : `${fileProduct}-${channel}-${templateId}-${dimensions}`;
  return `${name}.png`;
}

function buildTemplatePdfFilename(
  templateId: string,
  width: number,
  height: number,
  product: string,
  channel: string
) {
  const meta = findTemplateMeta(channel, templateId, product);
  let dimensions = meta.suffix ? `${meta.dimensions}${meta.suffix}` : `${width}x${height}`;
  if (channel === "print" && templateId === "Voucher") dimensions = "85x55mm";
  if (channel === "events" && templateId === "QRcodeA4") dimensions = "210x297mm";
  if (channel === "events" && templateId === "QRcodePrint") dimensions = "100x100mm";
  const name = meta.filename
    ? `${product}-${meta.filename}-${dimensions}`
    : `${product}-${channel}-${templateId}-${dimensions}`;
  return `${name}.pdf`;
}

function withFilenameSuffix(filename: string, suffix: string) {
  if (!suffix) return filename;
  const dot = filename.lastIndexOf(".");
  if (dot < 0) throw new Error(`Export filename has no extension: ${filename}`);
  return `${filename.slice(0, dot)}${suffix}${filename.slice(dot)}`;
}

function base64ToBlob(base64: string) {
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return new Blob([bytes], {type: "image/png"});
}

function buildTemplatePdf(template: ServerRenderedTemplate, channel: string) {
  const isVoucher = channel === "print" && template.templateId === "Voucher";
  const isEventsA4 = channel === "events" && template.templateId === "QRcodeA4";
  const isEventsQR10 = channel === "events" && template.templateId === "QRcodePrint";
  const pxToPt = (pixels: number) => (pixels * 72) / 96;
  const mmToPt = (millimeters: number) => (millimeters / 25.4) * 72;
  const widthPt = isVoucher
    ? mmToPt(85)
    : isEventsA4
      ? mmToPt(210)
      : isEventsQR10
        ? mmToPt(100)
        : pxToPt(template.width);
  const heightPt = isVoucher
    ? mmToPt(55)
    : isEventsA4
      ? mmToPt(297)
      : isEventsQR10
        ? mmToPt(100)
        : pxToPt(template.height);
  const pdf = new jsPDF({
    orientation: widthPt >= heightPt ? "landscape" : "portrait",
    unit: "pt",
    format: [widthPt, heightPt],
    compress: true,
  });
  pdf.addImage(
    `data:image/png;base64,${template.pdfPng || template.png}`,
    "PNG",
    0,
    0,
    widthPt,
    heightPt,
    undefined,
    "FAST"
  );
  return pdf.output("arraybuffer");
}

/**
 * Adds files to a zip folder and refuses a second file under one name: one
 * banner would silently replace another (two splash designs of one size both
 * export as `splash.png`).
 */
function uniqueFileAdder(folder: JSZip) {
  const names = new Set<string>();
  return (
    name: string,
    data: string | ArrayBuffer | Uint8Array | Blob,
    options?: JSZip.JSZipFileOptions
  ) => {
    if (names.has(name)) {
      throw new Error(`Two exported banners have the file name "${name}": one would replace the other`);
    }
    names.add(name);
    folder.file(name, data, options);
  };
}

/** Build the user download from one or more server-rendered scene batches. */
export async function buildServerExportBundle({
  batches,
  product,
  channel,
  title = "",
  set,
}: ServerExportOptions): Promise<ExportBundle> {
  if (!Array.isArray(batches) || batches.length === 0) {
    throw new Error("Server export has no render batches");
  }
  const entries = batches.flatMap(({templates: batchTemplates, suffix = ""}) => {
    if (!Array.isArray(batchTemplates) || batchTemplates.length === 0) {
      throw new Error("Server export batch has no templates");
    }
    return batchTemplates.map((template) => ({template, suffix}));
  });

  if (entries.length === 1) {
    const {template, suffix} = entries[0];
    const filename =
      isSplashSetExport(channel, set) && isSplashTemplate(template.templateId, product)
        ? splashVariantFilename(template.templateId)
      : withFilenameSuffix(
          buildTemplateFilename(
            template.templateId,
            template.width,
            template.height,
            product,
            channel
          ),
          suffix
        );
    return {
      blob: base64ToBlob(template.png),
      filename,
      isSingleFile: true,
    };
  }

  if (isSplashSetExport(channel, set)) {
    const zip = new JSZip();
    const folder = zip.folder(SPLASH_FOLDER);
    if (!folder) throw new Error("Could not create the splash export folder");
    const addSplashFile = uniqueFileAdder(folder);
    const addFile = uniqueFileAdder(zip);
    for (const {template, suffix} of entries) {
      if (isSplashTemplate(template.templateId, product)) {
        addSplashFile(splashVariantFilename(template.templateId), template.png, {base64: true});
        continue;
      }
      // A banner added to the splash page from another set keeps its own name.
      addFile(
        withFilenameSuffix(
          buildTemplateFilename(template.templateId, template.width, template.height, product, channel),
          suffix
        ),
        template.png,
        {base64: true}
      );
    }
    return {
      blob: await zip.generateAsync({type: "blob"}),
      filename: `${product}-${SPLASH_FOLDER}.zip`,
      isSingleFile: false,
    };
  }

  if (channel === "print" || channel === "events") {
    const zip = new JSZip();
    const addFile = uniqueFileAdder(zip);
    for (const {template, suffix} of entries) {
      const pngName = withFilenameSuffix(
        buildTemplateFilename(
          template.templateId,
          template.width,
          template.height,
          product,
          channel
        ),
        suffix
      );
      const pdfName = withFilenameSuffix(
        buildTemplatePdfFilename(
          template.templateId,
          template.width,
          template.height,
          product,
          channel
        ),
        suffix
      );
      addFile(pngName, template.png, {base64: true});
      addFile(pdfName, buildTemplatePdf(template, channel));
    }
    return {
      blob: await zip.generateAsync({type: "blob"}),
      filename: buildArchiveFilename(product, channel, title, Date.now()),
      isSingleFile: false,
    };
  }

  const zip = new JSZip();
  const addFile = uniqueFileAdder(zip);
  for (const {template, suffix} of entries) {
    addFile(
      withFilenameSuffix(
        buildTemplateFilename(
          template.templateId,
          template.width,
          template.height,
          product,
          channel
        ),
        suffix
      ),
      template.png,
      {base64: true}
    );
  }
  return {
    blob: await zip.generateAsync({type: "blob"}),
    filename: buildArchiveFilename(product, channel, title, Date.now()),
    isSingleFile: false,
  };
}

function parseRenderedTemplates(data: unknown): ServerRenderedTemplate[] {
  if (!data || typeof data !== "object" || !Array.isArray((data as any).templates)) {
    throw new Error("Server render response has no templates array");
  }
  const rendered = (data as any).templates;
  if (rendered.length === 0) throw new Error("Server render returned no templates");
  for (const [index, template] of rendered.entries()) {
    const valid =
      template &&
      typeof template.templateId === "string" &&
      template.templateId.length > 0 &&
      Number.isFinite(template.width) &&
      template.width > 0 &&
      Number.isFinite(template.height) &&
      template.height > 0 &&
      typeof template.png === "string" &&
      template.png.length > 0 &&
      (template.pdfPng === undefined || typeof template.pdfPng === "string");
    if (!valid) throw new Error(`Server render returned invalid template at index ${index}`);
  }
  return rendered as ServerRenderedTemplate[];
}

export async function renderServerScene(
  scene: Record<string, unknown>,
  fetchRender: FetchServerRender = fetch
): Promise<ServerRenderedTemplate[]> {
  const response = await fetchRender("/render/batch", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify({scene}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(`Server render failed (${response.status}): ${JSON.stringify(data)}`);
  }
  return parseRenderedTemplates(data);
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function downloadServerExportBundle(options: ServerExportOptions) {
  const result = await buildServerExportBundle(options);
  downloadBlob(result.blob, result.filename);
  document.body.dataset.zip = result.filename;
}
